import type { ReactElement } from "react";

import { Button } from "@realroof/ui";

import { MODULE_LABEL, ROLE_LABEL, type Module } from "./api";
import { Analytics } from "./pages/Analytics";
import { Boxes } from "./pages/Boxes";
import { HomePageAdmin } from "./pages/HomePage";
import { Audit } from "./pages/Audit";
import { Login } from "./pages/Login";
import { Coupons } from "./pages/Coupons";
import { Locations } from "./pages/Locations";
import { MyAccount } from "./pages/MyAccount";
import { Orders } from "./pages/Orders";
import { Plans } from "./pages/Plans";
import { Queue } from "./pages/Queue";
import { Reports } from "./pages/Reports";
import { Review } from "./pages/Review";
import { Pros } from "./pages/Pros";
import { Reviews } from "./pages/Reviews";
import { Settings } from "./pages/Settings";
import { Staff, StaffActivity } from "./pages/Staff";
import { Subscribers } from "./pages/Subscribers";
import { Link, useLocation } from "./router";
import { SessionProvider, useSession } from "./session";

const PATH: Record<Module, string> = {
  ads: "ads",
  reports: "reports",
  reviews: "reviews",
  pros: "builders",
  boxes: "boxes",
  home: "home-page",
  orders: "orders",
  plans: "plans",
  coupons: "coupons",
  locations: "locations",
  subscribers: "subscribers",
  analytics: "analytics",
  settings: "settings",
  audit: "audit",
};
const PAGES: Record<Module, () => ReactElement> = {
  ads: () => <Queue />,
  reports: () => <Reports />,
  reviews: () => <Reviews />,
  pros: () => <Pros />,
  boxes: () => <Boxes />,
  home: () => <HomePageAdmin />,
  orders: () => <Orders />,
  plans: () => <Plans />,
  coupons: () => <Coupons />,
  locations: () => <Locations />,
  subscribers: () => <Subscribers />,
  analytics: () => <Analytics />,
  settings: () => <Settings />,
  audit: () => <Audit />,
};

function Shell() {
  const { me, loading, logOut } = useSession();
  const { path } = useLocation();
  if (loading) return <p className="p-6 text-muted-foreground">Loading…</p>;
  if (!me) return <Login />;

  // Each section is shown only to staff who have it (the super admin can narrow a person's sections)
  const allowed = new Set(me.modules);
  const ad = /^\/ads\/([0-9a-f-]{36})$/.exec(path);
  const member = /^\/staff\/([0-9a-f-]{36})$/.exec(path);
  const super_ = me.staff_role === "super_admin";
  let page;
  if (ad && allowed.has("ads")) page = <Review id={ad[1]} />;
  else if (member && super_) page = <StaffActivity id={member[1]} />;
  else if (path === "/staff" && super_) page = <Staff />;
  else if (path === "/account") page = <MyAccount />;
  else {
    const section = (Object.keys(PAGES) as Module[]).find((m) => allowed.has(m) && path === `/${PATH[m]}`);
    const first = me.modules[0];
    page = section ? PAGES[section]() : allowed.has("ads") ? <Queue /> : first ? PAGES[first]() : <MyAccount />;
  }

  const nav = [...me.modules.map((m) => [`/${PATH[m]}`, MODULE_LABEL[m]]), ...(super_ ? [["/staff", "Staff"]] : [])];
  return (
    <div className="grid min-h-dvh md:grid-cols-[220px_1fr]">
      {/* wide screens: the sidebar stays put (full height, never scrolls with the page); logo at the top,
          the account and Log out pinned at the bottom; only the menu list scrolls if it ever gets too long */}
      <nav
        className="flex flex-wrap items-center gap-1 bg-sidebar px-3 py-2.5 text-sidebar-foreground md:sticky md:top-0 md:h-dvh md:flex-col md:flex-nowrap md:items-stretch md:px-3 md:py-5"
        aria-label="Main"
      >
        <strong className="mr-3 flex items-center gap-2 font-display text-lg text-white md:mb-3 md:mr-0 md:px-2.5">
          <span aria-hidden="true" className="grid size-7 place-items-center rounded-md bg-primary text-sm text-primary-foreground">
            R
          </span>
          RealRoof Admin
        </strong>
        <div className="contents md:flex md:min-h-0 md:flex-1 md:flex-col md:gap-1 md:overflow-y-auto">
        {nav.map(([to, label]) => (
          <Link
            key={to}
            to={to}
            className="rounded-md px-2.5 py-2 text-sidebar-foreground no-underline hover:bg-white/10 aria-[current=page]:bg-sidebar-active aria-[current=page]:text-white"
            aria-current={path.startsWith(to) || (to === "/ads" && path === "/") ? "page" : undefined}
          >
            {label}
          </Link>
        ))}
        </div>
        <div className="ml-auto text-[0.82rem] md:mt-3 md:ml-0 md:shrink-0 md:border-t md:border-white/10 md:px-2.5 md:pt-3">
          <Link to="/account" className="text-white underline-offset-2 hover:underline" aria-label="My account">
            {me.full_name || me.email}
          </Link>
          <span className="opacity-75"> · {ROLE_LABEL[me.staff_role]}</span>
          <br />
          <Button variant="link" className="text-sidebar-foreground underline" onClick={logOut}>
            Log out
          </Button>
        </div>
      </nav>
      <main className="min-w-0 p-4 md:p-6">{page}</main>
    </div>
  );
}

export function App() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
